/**
 * Conversations that have left the tab strip.
 *
 * A conversation lands here once support has had the last word and the reporter
 * has been silent for a week. No status is shown: from this side it is a chat,
 * and whether a case is open is the team's business, not something the reporter
 * should have to read. Nothing is deleted either — any new message on a
 * conversation puts it back among the tabs.
 */
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SupportTicket } from "../../api/support-tickets";
import { formatFriendlyDate } from "../../utils/date-formatting";

interface SupportHistoryListProps {
	tickets: SupportTicket[];
	onOpen: (ticket: SupportTicket) => void;
}

const SupportHistoryList: React.FC<SupportHistoryListProps> = ({ tickets, onOpen }) => (
	<Stack
		sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
		data-component="support-history-list"
	>
		<List disablePadding>
			{tickets.map((ticket) => (
				<ListItemButton
					key={ticket.id}
					divider
					onClick={() => onOpen(ticket)}
					data-component="support-history-row"
				>
					<ListItemText
						primary={ticket.title}
						secondary={formatFriendlyDate(ticket.updated)}
						slotProps={{
							primary: { noWrap: true, variant: "body2" },
							secondary: { variant: "caption" },
						}}
					/>
				</ListItemButton>
			))}
			{tickets.length === 0 && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ px: 2, py: 3 }}
				>
					Nothing older yet.
				</Typography>
			)}
		</List>
	</Stack>
);

export default SupportHistoryList;
